import { useState, useEffect, useMemo, useCallback, lazy, Suspense } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Search, TrendingUp, MapPin, Users, ChevronRight, 
  BarChart3, Target, ArrowUpRight, ArrowDownRight,
  Crown, Filter, RefreshCw, SlidersHorizontal, Map, List, Globe
} from 'lucide-react';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';

// Lazy load the map component to avoid SSR issues
const UKConstituencyMap = lazy(() => import('../components/UKConstituencyMap'));

const API = process.env.REACT_APP_BACKEND_URL;

// 2024 General Election Results (baseline)
const ELECTION_2024_NATIONAL = {
  labour: { votes: 34.7, seats: 412 },
  conservative: { votes: 24.4, seats: 121 },
  libdem: { votes: 12.6, seats: 72 },
  reform: { votes: 14.7, seats: 5 },
  green: { votes: 6.9, seats: 4 },
  snp: { votes: 2.6, seats: 9 },
  plaid: { votes: 0.7, seats: 4 },
  other: { votes: 3.4, seats: 23 }
};

// Helper to create slug from name
const createSlug = (name) => {
  if (!name) return '';
  return name.toLowerCase()
    .replace(/^(sir|dame|dr|mr|ms|mrs)\s+/i, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
};

// Party colors and labels
const PARTY_CONFIG = {
  labour: { color: '#E4003B', label: 'Labour', shortLabel: 'LAB' },
  conservative: { color: '#0087DC', label: 'Conservative', shortLabel: 'CON' },
  libdem: { color: '#FAA61A', label: 'Lib Dem', shortLabel: 'LD' },
  reform: { color: '#12B6CF', label: 'Reform UK', shortLabel: 'REF' },
  green: { color: '#6AB023', label: 'Green', shortLabel: 'GRN' },
  snp: { color: '#FDF38E', label: 'SNP', shortLabel: 'SNP' },
  plaid: { color: '#005B54', label: 'Plaid Cymru', shortLabel: 'PC' },
  dup: { color: '#D46A4C', label: 'DUP', shortLabel: 'DUP' },
  sinnfein: { color: '#326760', label: 'Sinn Féin', shortLabel: 'SF' },
  other: { color: '#808080', label: 'Other', shortLabel: 'OTH' }
};

// Latest polling averages (Feb 2026 from Wikipedia)
const DEFAULT_POLLING = {
  reform: 29,
  labour: 19,
  conservative: 18,
  green: 15,
  libdem: 12,
  snp: 3,
  plaid: 1,
  other: 3
};

const ElectionOddsPage = () => {
  const [constituencies, setConstituencies] = useState([]);
  const [rawMPs, setRawMPs] = useState([]);
  const [polling, setPolling] = useState(DEFAULT_POLLING);
  const [customPolling, setCustomPolling] = useState(DEFAULT_POLLING);
  const [electionData, setElectionData] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [filterParty, setFilterParty] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('margin');
  const [viewMode, setViewMode] = useState('list'); // list, map, geomap
  const [showWhatIf, setShowWhatIf] = useState(false);
  const navigate = useNavigate();

  // Handle constituency click from map
  const handleConstituencyClick = (constituency) => {
    if (constituency.currentMP) {
      const slug = createSlug(constituency.currentMP);
      navigate(`/politician/${slug}`);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Recalculate constituencies when custom polling changes
  const recalculateWithCustomPolling = useCallback(() => {
    if (rawMPs.length === 0) return;
    const constituencyData = rawMPs.map(mp => 
      generateConstituencyPrediction(mp, electionData, customPolling)
    );
    setConstituencies(constituencyData);
  }, [rawMPs, electionData, customPolling]);

  useEffect(() => {
    if (showWhatIf && rawMPs.length > 0) {
      recalculateWithCustomPolling();
    }
  }, [customPolling, showWhatIf, recalculateWithCustomPolling]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch real 2024 election results
      let election2024 = {};
      try {
        const electionResponse = await axios.get(`${API}/api/election-results/2024`);
        if (electionResponse.data?.results) {
          // Create a lookup by constituency name
          electionResponse.data.results.forEach(r => {
            election2024[r.constituency.toLowerCase()] = r;
          });
          setElectionData(election2024);
          console.log('Loaded 2024 election data:', Object.keys(election2024).length);
        }
      } catch (e) {
        console.log('Could not load 2024 election data, using simulated data');
      }

      // Fetch MPs/constituencies
      const mpsResponse = await axios.get(`${API}/api/mps?limit=700`);
      const mps = mpsResponse.data?.mps || mpsResponse.data || [];
      console.log('Fetched MPs:', mps.length);
      setRawMPs(mps); // Store raw MPs for recalculation
      
      // Fetch current polling
      let currentPolling = DEFAULT_POLLING;
      try {
        const pollResponse = await axios.get(`${API}/api/polls/latest`);
        if (pollResponse.data?.polling_average) {
          currentPolling = {
            reform: pollResponse.data.polling_average.reform || DEFAULT_POLLING.reform,
            labour: pollResponse.data.polling_average.labour || DEFAULT_POLLING.labour,
            conservative: pollResponse.data.polling_average.conservative || DEFAULT_POLLING.conservative,
            green: pollResponse.data.polling_average.green || DEFAULT_POLLING.green,
            libdem: pollResponse.data.polling_average.libdem || DEFAULT_POLLING.libdem,
            snp: pollResponse.data.polling_average.snp || DEFAULT_POLLING.snp,
            plaid: pollResponse.data.polling_average.plaid || DEFAULT_POLLING.plaid,
            other: pollResponse.data.polling_average.other || DEFAULT_POLLING.other
          };
          setPolling(currentPolling);
          setCustomPolling(currentPolling);
        }
      } catch (e) {
        console.log('Using default polling data');
      }
      
      // Transform MPs into constituency predictions using real 2024 data
      const constituencyData = mps.map(mp => 
        generateConstituencyPrediction(mp, election2024, currentPolling)
      );
      setConstituencies(constituencyData);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  };

  // Normalize party name to match our standard keys
  const normalizeParty = (party) => {
    if (!party) return 'other';
    const p = party.toLowerCase().replace(/_/g, ' ').replace(/\s+/g, '');
    if (p.includes('labour')) return 'labour';
    if (p.includes('conservative')) return 'conservative';
    if (p.includes('libdem') || p.includes('liberaldemocrat')) return 'libdem';
    if (p.includes('reform')) return 'reform';
    if (p.includes('green')) return 'green';
    if (p.includes('snp') || p.includes('scottishnationalparty')) return 'snp';
    if (p.includes('plaid')) return 'plaid';
    if (p.includes('dup') || p.includes('democraticunionist')) return 'dup';
    if (p.includes('sinnfein') || p.includes('sinnféin')) return 'sinnfein';
    return 'other';
  };

  // Generate prediction for a constituency based on UNS model with REAL 2024 data
  const generateConstituencyPrediction = (mp, election2024, currentPolling) => {
    const constituencyKey = (mp.constituency || '').toLowerCase();
    const real2024 = election2024[constituencyKey];
    
    // Get current party (normalize)
    const currentParty = normalizeParty(mp.party);
    
    let baseVotes;
    if (real2024 && real2024.votes) {
      // Use REAL 2024 results
      baseVotes = {
        labour: real2024.votes.labour || 0,
        conservative: real2024.votes.conservative || 0,
        libdem: real2024.votes.libdem || 0,
        reform: real2024.votes.reform || 0,
        green: real2024.votes.green || 0,
        snp: real2024.votes.snp || 0,
        plaid: real2024.votes.plaid || 0,
        dup: real2024.votes.dup || 0,
        sinnfein: real2024.votes.sinnfein || 0,
        other: real2024.votes.other || 0
      };
    } else {
      // Fallback to simulated data if no real data
      baseVotes = generateBaseVotes(currentParty, mp.region);
    }
    
    // Apply Uniform National Swing using current polling vs 2024 results
    const predictedVotes = applySwing(baseVotes, currentPolling);
    
    // Determine predicted winner and margin
    const sortedParties = Object.entries(predictedVotes)
      .filter(([, v]) => v > 0)
      .sort(([,a], [,b]) => b - a);
    
    const predictedWinner = sortedParties[0]?.[0] || currentParty;
    const winnerVotes = sortedParties[0]?.[1] || 0;
    const secondVotes = sortedParties[1]?.[1] || 0;
    const margin = winnerVotes - secondVotes;
    
    // Calculate swing needed to flip
    const swingNeeded = margin / 2;
    
    // Determine status
    let status = 'safe';
    if (margin < 5) status = 'tossup';
    else if (margin < 10) status = 'marginal';
    else if (margin < 20) status = 'leaning';
    
    // Calculate probability (simplified)
    const probability = Math.min(99, Math.max(1, 50 + (margin * 2.5)));
    
    return {
      id: mp.id,
      name: mp.constituency || mp.name,
      region: real2024?.region || mp.region,
      currentMP: mp.name,
      currentParty,
      votes2024: baseVotes,
      predictedVotes,
      predictedWinner,
      margin: margin.toFixed(1),
      swingNeeded: swingNeeded.toFixed(1),
      status,
      probability: probability.toFixed(0),
      isFlip: predictedWinner !== currentParty,
      hasRealData: !!real2024
    };
  };

  // Generate realistic 2024 baseline votes (fallback when real data unavailable)
  const generateBaseVotes = (winningParty, region) => {
    const isScotland = region?.toLowerCase().includes('scotland');
    const isWales = region?.toLowerCase().includes('wales');
    
    // Base distribution varies by region
    let votes = {
      labour: 25 + Math.random() * 20,
      conservative: 20 + Math.random() * 20,
      libdem: 8 + Math.random() * 15,
      reform: 10 + Math.random() * 10,
      green: 3 + Math.random() * 8,
      snp: isScotland ? 30 + Math.random() * 20 : 0,
      plaid: isWales ? 10 + Math.random() * 15 : 0,
      other: 2 + Math.random() * 5
    };
    
    // Boost winning party to ensure they won in 2024
    votes[winningParty] = Math.max(votes[winningParty], 35 + Math.random() * 15);
    
    // Normalize to 100%
    const total = Object.values(votes).reduce((a, b) => a + b, 0);
    Object.keys(votes).forEach(party => {
      votes[party] = (votes[party] / total) * 100;
    });
    
    return votes;
  };

  // Apply Uniform National Swing based on polling changes
  // Apply Uniform National Swing based on polling changes
  const applySwing = (baseVotes, currentPolling) => {
    const swings = {};
    const pollData = currentPolling || polling;
    
    // Calculate swing from 2024 to current polling
    Object.keys(ELECTION_2024_NATIONAL).forEach(party => {
      const swing2024 = ELECTION_2024_NATIONAL[party]?.votes || 0;
      const currentPoll = pollData[party] || 0;
      swings[party] = currentPoll - swing2024;
    });
    
    // Apply swings to base votes
    const newVotes = {};
    Object.keys(baseVotes).forEach(party => {
      const swing = swings[party] || 0;
      newVotes[party] = Math.max(0, baseVotes[party] + swing);
    });
    
    // Normalize
    const total = Object.values(newVotes).reduce((a, b) => a + b, 0);
    if (total > 0) {
      Object.keys(newVotes).forEach(party => {
        newVotes[party] = (newVotes[party] / total) * 100;
      });
    }
    
    return newVotes;
  };

  // Calculate national seat projections
  const seatProjections = useMemo(() => {
    if (!constituencies.length) return {};
    
    const projections = {};
    constituencies.forEach(c => {
      const winner = c.predictedWinner;
      projections[winner] = (projections[winner] || 0) + 1;
    });
    
    return projections;
  }, [constituencies]);

  // Filter and sort constituencies
  const filteredConstituencies = useMemo(() => {
    let filtered = constituencies;
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(c => 
        c.name?.toLowerCase().includes(query) ||
        c.currentMP?.toLowerCase().includes(query) ||
        c.region?.toLowerCase().includes(query)
      );
    }
    
    // Party filter
    if (filterParty !== 'all') {
      filtered = filtered.filter(c => c.predictedWinner === filterParty);
    }
    
    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(c => c.status === filterStatus);
    }
    
    // Sort
    filtered.sort((a, b) => {
      if (sortBy === 'margin') return parseFloat(a.margin) - parseFloat(b.margin);
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      if (sortBy === 'swing') return parseFloat(a.swingNeeded) - parseFloat(b.swingNeeded);
      return 0;
    });
    
    return filtered;
  }, [constituencies, searchQuery, filterParty, filterStatus, sortBy]);

  // Count stats
  const stats = useMemo(() => {
    const flips = constituencies.filter(c => c.isFlip).length;
    const tossups = constituencies.filter(c => c.status === 'tossup').length;
    const marginals = constituencies.filter(c => c.status === 'marginal').length;
    return { flips, tossups, marginals };
  }, [constituencies]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Calculating predictions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-20" data-testid="election-odds-page">
      <SEO 
        title="UK Election Odds & Seat Predictions"
        description="See predicted winners for all 650 UK parliamentary seats. Live odds based on current polling and historical data. Find out who's likely to win your constituency."
        keywords="UK election predictions, seat calculator, constituency odds, election forecast, who will win my seat, MP predictions"
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white py-10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-amber-500/20 p-2 rounded-xl">
              <Target className="h-6 w-6 text-amber-400" />
            </div>
            <h1 className="text-2xl md:text-3xl font-black">Election Odds</h1>
          </div>
          <p className="text-slate-300 max-w-2xl">
            Predicted winners for all UK parliamentary seats based on current polling and uniform national swing.
          </p>
          
          {/* National Projection Summary */}
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {Object.entries(seatProjections)
              .sort(([,a], [,b]) => b - a)
              .slice(0, 7)
              .map(([party, seats]) => {
                const config = PARTY_CONFIG[party] || PARTY_CONFIG.other;
                const change = seats - (ELECTION_2024_NATIONAL[party]?.seats || 0);
                return (
                  <div 
                    key={party}
                    className="bg-white/10 backdrop-blur rounded-xl p-3 text-center"
                  >
                    <div 
                      className="w-3 h-3 rounded-full mx-auto mb-1"
                      style={{ backgroundColor: config.color }}
                    />
                    <div className="text-xs text-slate-400">{config.shortLabel}</div>
                    <div className="text-2xl font-black">{seats}</div>
                    <div className={`text-xs ${change > 0 ? 'text-green-400' : change < 0 ? 'text-red-400' : 'text-slate-400'}`}>
                      {change > 0 ? '+' : ''}{change}
                    </div>
                  </div>
                );
              })}
          </div>
          
          {/* Key Stats */}
          <div className="mt-6 flex flex-wrap gap-4">
            <div className="bg-white/10 backdrop-blur rounded-lg px-4 py-2">
              <span className="text-amber-400 font-bold">{stats.flips}</span>
              <span className="text-slate-300 text-sm ml-2">Predicted Flips</span>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-lg px-4 py-2">
              <span className="text-red-400 font-bold">{stats.tossups}</span>
              <span className="text-slate-300 text-sm ml-2">Toss-ups (&lt;5%)</span>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-lg px-4 py-2">
              <span className="text-orange-400 font-bold">{stats.marginals}</span>
              <span className="text-slate-300 text-sm ml-2">Marginals (&lt;10%)</span>
            </div>
            <Button
              variant="outline"
              onClick={() => setShowWhatIf(!showWhatIf)}
              className="ml-auto bg-amber-500/20 border-amber-500/50 text-amber-300 hover:bg-amber-500/30"
              data-testid="whatif-toggle"
            >
              <SlidersHorizontal className="h-4 w-4 mr-2" />
              {showWhatIf ? 'Hide' : 'What If?'}
            </Button>
          </div>

          {/* What If? Polling Sliders */}
          {showWhatIf && (
            <div className="mt-6 bg-white/10 backdrop-blur rounded-xl p-6" data-testid="whatif-panel">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-white flex items-center gap-2">
                  <SlidersHorizontal className="h-5 w-5" />
                  Adjust Polling - See How Seats Change
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCustomPolling(polling)}
                  className="text-slate-300 hover:text-white"
                >
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Reset
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {['reform', 'labour', 'conservative', 'libdem', 'green', 'snp'].map(party => {
                  const config = PARTY_CONFIG[party];
                  return (
                    <div key={party} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: config.color }}
                          />
                          <span className="text-sm text-slate-300">{config.label}</span>
                        </div>
                        <span className="text-white font-bold">{customPolling[party]}%</span>
                      </div>
                      <Slider
                        value={[customPolling[party]]}
                        onValueChange={([value]) => setCustomPolling(prev => ({...prev, [party]: value}))}
                        min={0}
                        max={50}
                        step={1}
                        className="[&_[role=slider]]:bg-white"
                      />
                      <div className="text-xs text-slate-500">
                        2024: {ELECTION_2024_NATIONAL[party]?.votes}%
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-slate-400 mt-4">
                Drag sliders to see how seat projections change. Based on Uniform National Swing model.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* View Toggle */}
      <section className="max-w-7xl mx-auto px-4 pb-4">
        <div className="flex gap-2">
          <Button
            variant={viewMode === 'list' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('list')}
            data-testid="view-list"
          >
            <List className="h-4 w-4 mr-1" />
            List
          </Button>
          <Button
            variant={viewMode === 'map' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('map')}
            data-testid="view-map"
          >
            <Map className="h-4 w-4 mr-1" />
            Regions
          </Button>
          <Button
            variant={viewMode === 'geomap' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('geomap')}
            data-testid="view-geomap"
          >
            <Globe className="h-4 w-4 mr-1" />
            UK Map
          </Button>
        </div>
      </section>

      {/* Geographic Map View */}
      {viewMode === 'geomap' && (
        <section className="max-w-7xl mx-auto px-4 pb-6">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
            <h3 className="font-bold text-lg mb-4">UK Constituency Map - Predicted Winners</h3>
            <p className="text-sm text-slate-500 mb-4">
              Click on a constituency to view the MP's profile. Hover for details.
            </p>
            <Suspense fallback={
              <div className="h-[500px] bg-slate-100 rounded-xl flex items-center justify-center">
                <div className="text-slate-500">Loading map...</div>
              </div>
            }>
              <UKConstituencyMap 
                constituencies={constituencies}
                onConstituencyClick={handleConstituencyClick}
              />
            </Suspense>
          </div>
        </section>
      )}

      {/* Region Map View */}
      {viewMode === 'map' && (
        <section className="max-w-7xl mx-auto px-4 pb-6">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h3 className="font-bold text-lg mb-4">Predicted Winners by Region</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {Object.entries(
                constituencies.reduce((acc, c) => {
                  const region = c.region || 'Unknown';
                  if (!acc[region]) acc[region] = { total: 0, parties: {} };
                  acc[region].total++;
                  acc[region].parties[c.predictedWinner] = (acc[region].parties[c.predictedWinner] || 0) + 1;
                  return acc;
                }, {})
              ).sort((a, b) => b[1].total - a[1].total).map(([region, data]) => {
                const topParty = Object.entries(data.parties).sort(([,a], [,b]) => b - a)[0];
                const config = PARTY_CONFIG[topParty?.[0]] || PARTY_CONFIG.other;
                return (
                  <div 
                    key={region} 
                    className="p-4 rounded-lg border border-slate-200 hover:shadow-md transition-shadow"
                    style={{ borderLeftColor: config.color, borderLeftWidth: '4px' }}
                  >
                    <div className="font-semibold text-sm truncate">{region}</div>
                    <div className="text-xs text-slate-500">{data.total} seats</div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {Object.entries(data.parties)
                        .sort(([,a], [,b]) => b - a)
                        .slice(0, 4)
                        .map(([party, count]) => {
                          const pConfig = PARTY_CONFIG[party] || PARTY_CONFIG.other;
                          return (
                            <span 
                              key={party}
                              className="text-xs px-2 py-0.5 rounded-full text-white"
                              style={{ backgroundColor: pConfig.color }}
                            >
                              {count}
                            </span>
                          );
                        })}
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Legend */}
            <div className="mt-6 flex flex-wrap gap-4 justify-center">
              {Object.entries(PARTY_CONFIG).slice(0, 7).map(([party, config]) => (
                <div key={party} className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: config.color }} />
                  <span className="text-xs text-slate-600">{config.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Search and Filters - Only show in list view */}
      {viewMode === 'list' && (
      <>
      <section className="max-w-7xl mx-auto px-4 py-6">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search constituency, MP, or region..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                data-testid="constituency-search"
              />
            </div>
            
            {/* Party Filter */}
            <select
              value={filterParty}
              onChange={(e) => setFilterParty(e.target.value)}
              className="px-4 py-2.5 border border-slate-200 rounded-lg bg-white"
            >
              <option value="all">All Parties</option>
              {Object.entries(PARTY_CONFIG).map(([key, config]) => (
                <option key={key} value={key}>{config.label}</option>
              ))}
            </select>
            
            {/* Status Filter */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2.5 border border-slate-200 rounded-lg bg-white"
            >
              <option value="all">All Seats</option>
              <option value="tossup">Toss-up (&lt;5%)</option>
              <option value="marginal">Marginal (&lt;10%)</option>
              <option value="leaning">Leaning (&lt;20%)</option>
              <option value="safe">Safe (20%+)</option>
            </select>
            
            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2.5 border border-slate-200 rounded-lg bg-white"
            >
              <option value="margin">Sort by Margin</option>
              <option value="name">Sort by Name</option>
              <option value="swing">Sort by Swing Needed</option>
            </select>
          </div>
          
          <div className="mt-3 text-sm text-slate-500">
            Showing {filteredConstituencies.length} of {constituencies.length} constituencies
          </div>
        </div>
      </section>

      {/* Constituency List */}
      <section className="max-w-7xl mx-auto px-4 pb-10">
        <div className="grid gap-3">
          {filteredConstituencies.slice(0, 100).map((constituency) => {
            const config = PARTY_CONFIG[constituency.predictedWinner] || PARTY_CONFIG.other;
            const currentConfig = PARTY_CONFIG[constituency.currentParty] || PARTY_CONFIG.other;
            const mpSlug = createSlug(constituency.currentMP);
            
            return (
              <Link
                to={`/politician/${mpSlug}`}
                key={constituency.id}
                className={`bg-white rounded-xl border ${constituency.isFlip ? 'border-amber-300 shadow-md' : 'border-slate-200'} p-4 hover:shadow-lg transition-shadow block`}
                data-testid={`constituency-${constituency.id}`}
              >
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  {/* Constituency Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-lg">{constituency.name}</h3>
                      {constituency.isFlip && (
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                          FLIP
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3.5 w-3.5" />
                        {constituency.region}
                      </span>
                      <span>Current MP: {constituency.currentMP}</span>
                    </div>
                  </div>
                  
                  {/* Current vs Predicted */}
                  <div className="flex items-center gap-4">
                    {/* Current Holder */}
                    <div className="text-center">
                      <div className="text-xs text-slate-400 mb-1">2024 Winner</div>
                      <div 
                        className="px-3 py-1 rounded-full text-white text-sm font-bold"
                        style={{ backgroundColor: currentConfig.color }}
                      >
                        {currentConfig.shortLabel}
                      </div>
                    </div>
                    
                    {/* Arrow */}
                    <ChevronRight className={`h-5 w-5 ${constituency.isFlip ? 'text-amber-500' : 'text-slate-300'}`} />
                    
                    {/* Predicted Winner */}
                    <div className="text-center">
                      <div className="text-xs text-slate-400 mb-1">Predicted</div>
                      <div 
                        className="px-3 py-1 rounded-full text-white text-sm font-bold"
                        style={{ backgroundColor: config.color }}
                      >
                        {config.shortLabel}
                      </div>
                    </div>
                  </div>
                  
                  {/* Stats */}
                  <div className="flex items-center gap-6 text-center">
                    <div>
                      <div className="text-xs text-slate-400">Margin</div>
                      <div className={`font-bold ${parseFloat(constituency.margin) < 5 ? 'text-red-600' : parseFloat(constituency.margin) < 10 ? 'text-orange-600' : 'text-slate-900'}`}>
                        {constituency.margin}%
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-400">Swing to Flip</div>
                      <div className="font-bold text-slate-900">{constituency.swingNeeded}%</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-400">Confidence</div>
                      <div className="font-bold text-blue-600">{constituency.probability}%</div>
                    </div>
                  </div>
                </div>
                
                {/* Vote Share Bar */}
                <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden flex">
                  {Object.entries(constituency.predictedVotes)
                    .filter(([, votes]) => votes > 0.5)
                    .sort(([,a], [,b]) => b - a)
                    .map(([party, votes]) => (
                      <div
                        key={party}
                        className="h-full"
                        style={{ 
                          width: `${votes}%`, 
                          backgroundColor: PARTY_CONFIG[party]?.color || '#808080'
                        }}
                        title={`${PARTY_CONFIG[party]?.label}: ${votes.toFixed(1)}%`}
                      />
                    ))}
                </div>
              </Link>
            );
          })}
        </div>
        
        {filteredConstituencies.length > 100 && (
          <div className="text-center mt-6 text-slate-500">
            Showing first 100 results. Use search to find specific constituencies.
          </div>
        )}
      </section>
      </>
      )}

      {/* Methodology Note */}
      <section className="max-w-7xl mx-auto px-4 pb-10">
        <div className="bg-slate-100 rounded-xl p-6">
          <h3 className="font-bold mb-2">Methodology</h3>
          <p className="text-sm text-slate-600">
            Predictions use <strong>actual 2024 General Election results</strong> from the UK Parliament, combined with a Uniform National Swing (UNS) model based on current polling. 
            This assumes swing is uniform across all constituencies - actual results may vary based on local factors, candidate strength, and tactical voting.
            Margins below 5% are classified as "toss-ups" with high uncertainty.
          </p>
          <p className="text-xs text-slate-500 mt-2">
            Data source: UK Parliament Election Results (electionresults.parliament.uk)
          </p>
        </div>
      </section>
    </div>
  );
};

export default ElectionOddsPage;
